import { useState } from "react";
import { ChevronDown, ChevronUp, Zap } from "lucide-react";

export const PRIORITY_STYLES = {
  P0: { bg: "#7F1D1D", color: "#FFFFFF", label: "Critique — intervention urgente" },
  P1: { bg: "#FEE2E2", color: "#B91C1C", label: "Élevé" },
  P2: { bg: "#FEF3C7", color: "#92400E", label: "Moyen" },
  P3: { bg: "#E2E8F0", color: "#475569", label: "Optimisation" },
};

export function PriorityBadge({ scoring }) {
  const st = PRIORITY_STYLES[scoring?.priority];
  if (!st) return null;
  return (
    <span data-testid="issue-p-badge" title={scoring.reasoning?.priority_reason || st.label}
      className="text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
      style={{ background: st.bg, color: st.color }}>
      {scoring.priority}
    </span>
  );
}

const PRIO_ORDER = { P0: 0, P1: 1, P2: 2, P3: 3 };
const IMPACT_ORDER = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
const CONF_ORDER = { HIGH: 0, MEDIUM: 1, UNKNOWN: 2, LOW: 3 };

// Tri « À traiter en premier » — ordre documenté : P0→P3, puis impact, puis confiance,
// puis portée décroissante, puis quick wins. Issues sans scoring (anciens audits) en fin.
export function sortByPriorityFirst(issues) {
  return [...issues].sort((a, b) => {
    const sa = a.scoring, sb = b.scoring;
    if (!sa && !sb) return 0;
    if (!sa) return 1;
    if (!sb) return -1;
    return (PRIO_ORDER[sa.priority] - PRIO_ORDER[sb.priority])
      || (IMPACT_ORDER[sa.impact] - IMPACT_ORDER[sb.impact])
      || ((CONF_ORDER[sa.confidence] ?? 2) - (CONF_ORDER[sb.confidence] ?? 2))
      || ((sb.scope?.affected_ratio || 0) - (sa.scope?.affected_ratio || 0))
      || ((sb.quick_win ? 1 : 0) - (sa.quick_win ? 1 : 0));
  });
}

const scoreColor = (s) => (s >= 75 ? "#16A34A" : s >= 50 ? "#D97706" : "#DC2626");

function CategoryCard({ catKey, cat, selected, onSelect }) {
  const maxPrio = ["P0", "P1", "P2", "P3"].find((p) => cat.priorities?.[p] > 0);
  return (
    <button onClick={() => onSelect(selected === catKey ? null : catKey)}
      data-testid={`score-card-${catKey}`}
      className={`text-left border rounded-md p-3 bg-white transition-colors ${selected === catKey ? "border-[#002FA7] ring-1 ring-[#002FA7]/30" : "border-slate-200 hover:border-slate-400"}`}>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{cat.label}</div>
      {cat.status === "EVALUATED" ? (
        <>
          <div className="font-mono text-2xl font-semibold" style={{ color: scoreColor(cat.score) }}>
            {cat.score}<span className="text-sm text-slate-400">/100</span>
          </div>
          <div className="mt-1 text-[11px] text-slate-600 flex items-center gap-1.5 flex-wrap">
            <span>{cat.issues_total} issue(s)</span>
            {maxPrio && <span className="font-bold px-1 rounded" style={{ background: PRIORITY_STYLES[maxPrio].bg, color: PRIORITY_STYLES[maxPrio].color }}>{maxPrio}</span>}
          </div>
          <div className="text-[11px] text-slate-500">{cat.affected_urls}/{cat.evaluated_urls} URLs auditées affectées</div>
        </>
      ) : (
        <div data-testid={`score-card-${catKey}-not-evaluated`}>
          <div className="font-mono text-lg font-semibold text-slate-400">Non évalué</div>
          <div className="text-[11px] text-slate-500 mt-1">Aucune donnée fiable collectée — exclu du score global</div>
        </div>
      )}
    </button>
  );
}

export function ScoreOverview({ report, selectedCategory, onSelectCategory }) {
  const [detail, setDetail] = useState(false);
  const sc = report.scoring;
  if (!sc) {
    return (
      <div className="mb-4 border border-slate-200 bg-slate-50 rounded-md px-4 py-2.5 text-xs text-slate-600" data-testid="scoring-unavailable-note">
        Scoring détaillé (v1) non disponible pour cet audit (antérieur à la mise à jour) — relancez un audit pour obtenir sous-scores et priorités P0–P3.
      </div>
    );
  }
  const pt = sc.priorities_total || {};
  return (
    <div className="mb-6" data-testid="score-overview">
      <div className="grid md:grid-cols-[260px_1fr] gap-4">
        <div className="border border-slate-200 bg-white rounded-md p-5" data-testid="audit-score">
          <div className="overline mb-2">Score SEO global</div>
          {sc.global_score === null ? (
            <div className="font-mono text-2xl font-semibold text-slate-400">Non évaluable</div>
          ) : (
            <>
              <div className="font-mono text-4xl font-semibold" style={{ color: scoreColor(sc.global_score) }}>
                {sc.global_score}<span className="text-xl text-slate-400">/100</span>
              </div>
              <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${sc.global_score}%`, background: scoreColor(sc.global_score) }} />
              </div>
            </>
          )}
          <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]" data-testid="priorities-total">
            {["P0", "P1", "P2", "P3"].map((p) => (
              <span key={p} className="font-mono px-1.5 py-0.5 rounded" style={{ background: PRIORITY_STYLES[p].bg, color: PRIORITY_STYLES[p].color }}>
                {p}·{pt[p] ?? 0}
              </span>
            ))}
            {sc.quick_wins > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
                <Zap className="w-3 h-3" />{sc.quick_wins} quick win(s)
              </span>
            )}
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            {report.total_pages} page(s) · {sc.evaluated_urls} URLs auditées · scoring v{sc.scoring_version}
          </div>
          <button onClick={() => setDetail(!detail)} data-testid="score-detail-toggle"
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 border border-slate-300 rounded-md px-2 py-0.5 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors">
            {detail ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Voir le détail du score
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(sc.categories || {}).map(([k, cat]) => (
            <CategoryCard key={k} catKey={k} cat={cat} selected={selectedCategory} onSelect={onSelectCategory} />
          ))}
        </div>
      </div>
      {detail && (
        <div className="mt-3 border border-slate-200 bg-slate-50 rounded-md p-4" data-testid="score-detail-panel">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Principales pénalités (contribution au score global)</div>
          {(sc.main_penalties || []).length === 0 ? (
            <div className="text-sm text-slate-600">Aucune pénalité — aucun problème détecté sur les catégories évaluées.</div>
          ) : (
            <ul className="space-y-1.5">
              {sc.main_penalties.map((p, i) => (
                <li key={i} className="text-xs text-slate-700 flex items-start gap-2">
                  <span className="font-mono font-semibold text-red-700 whitespace-nowrap">−{p.global_contribution} pt</span>
                  <span>{p.reason}</span>
                </li>
              ))}
            </ul>
          )}
          {(sc.unavailable_categories || []).length > 0 && (
            <div className="mt-2 text-[11px] text-slate-500">
              Catégories non évaluées (exclues du calcul, poids renormalisés) : {sc.unavailable_categories.join(", ")}
            </div>
          )}
          <div className="mt-2 text-[11px] text-slate-500">
            Évolution vs audit précédent : NON DISPONIBLE (comparaison non calculée). Aucune donnée Google Search Console utilisée (Phase C à venir).
          </div>
        </div>
      )}
    </div>
  );
}

const DIM_LABELS = { LOW: "Faible", MEDIUM: "Moyen", HIGH: "Élevé", UNKNOWN: "Inconnu", CRITICAL: "Critique" };

export function IssueScoring({ scoring, idx }) {
  const [open, setOpen] = useState(false);
  if (!scoring) return null;
  return (
    <div className="mt-1.5">
      <button onClick={() => setOpen(!open)} data-testid={`issue-scoring-toggle-${idx}`}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 border border-slate-300 rounded-md px-2 py-0.5 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors">
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        Analyse de priorité
        {scoring.quick_win && <span className="inline-flex items-center gap-0.5 text-emerald-700"><Zap className="w-3 h-3" />Quick win</span>}
      </button>
      {open && (
        <div data-testid={`issue-scoring-panel-${idx}`} className="mt-1.5 border border-slate-200 bg-slate-50 rounded-md p-2.5 text-[11px] space-y-1">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
            <div><span className="text-slate-500">Impact SEO </span><span className="font-semibold text-slate-800">{DIM_LABELS[scoring.impact]}</span></div>
            <div><span className="text-slate-500">Confiance </span><span className="font-semibold text-slate-800">{DIM_LABELS[scoring.confidence] || scoring.confidence}</span></div>
            <div><span className="text-slate-500">Portée </span><span className="font-semibold text-slate-800">{scoring.scope?.affected_urls}/{scoring.scope?.evaluated_urls} URLs auditées</span></div>
            <div><span className="text-slate-500">Effort </span><span className="font-semibold text-slate-800">{DIM_LABELS[scoring.effort]}</span></div>
            <div><span className="text-slate-500">Risque de correction </span><span className="font-semibold text-slate-800">{DIM_LABELS[scoring.fix_risk]}</span></div>
            <div><span className="text-slate-500">Quick win </span><span className="font-semibold text-slate-800">{scoring.quick_win ? "oui" : "non"}</span></div>
          </div>
          <div className="pt-1 border-t border-slate-200 text-slate-700">{scoring.reasoning?.priority_reason}</div>
        </div>
      )}
    </div>
  );
}

export function FiltersBar({ filters, setFilters, sortOn, setSortOn, hasScoring }) {
  if (!hasScoring) return null;
  const chip = (active) =>
    `text-[11px] font-medium px-2 py-1 rounded-md border transition-colors ${active ? "border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"}`;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="audit-filters">
      <span className="text-[11px] text-slate-500 mr-1">Priorité</span>
      {["P0", "P1", "P2", "P3"].map((p) => (
        <button key={p} data-testid={`filter-priority-${p}`} className={chip(filters.priority === p)}
          onClick={() => setFilters((f) => ({ ...f, priority: f.priority === p ? null : p }))}>{p}</button>
      ))}
      <span className="text-[11px] text-slate-500 ml-2 mr-1">Confiance</span>
      {["HIGH", "MEDIUM", "LOW", "UNKNOWN"].map((c) => (
        <button key={c} data-testid={`filter-confidence-${c}`} className={chip(filters.confidence === c)}
          onClick={() => setFilters((f) => ({ ...f, confidence: f.confidence === c ? null : c }))}>{c}</button>
      ))}
      <button data-testid="filter-quick-wins" className={chip(filters.quickWins) + " ml-2 inline-flex items-center gap-1"}
        onClick={() => setFilters((f) => ({ ...f, quickWins: !f.quickWins }))}>
        <Zap className="w-3 h-3" /> Quick wins
      </button>
      <button data-testid="sort-priority-toggle" className={chip(sortOn) + " ml-2"}
        title="Ordre : P0→P3, puis impact, confiance, portée, quick wins"
        onClick={() => setSortOn(!sortOn)}>
        À traiter en premier
      </button>
      {(filters.priority || filters.confidence || filters.quickWins || filters.category) && (
        <button data-testid="filters-reset" className="text-[11px] text-slate-500 underline ml-1"
          onClick={() => setFilters({ priority: null, confidence: null, quickWins: false, category: null })}>
          Réinitialiser
        </button>
      )}
    </div>
  );
}
